import { TasksQueryDto } from '@nohara-dxdev/mt-common';
import { ApiResponse } from '@nohara-dxdev/oidc-core';
import {
  PaginatedQueryConfig,
  PaginationParams,
  PaginationResponse,
  usePaginatedQuery,
  UsePaginatedQueryOptions,
} from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';

import { api } from '@/lib/api-client';
import { TaskEntity } from '@/types/api';

export type GetTasksParams = TasksQueryDto & PaginationParams;

export const getTasks = async (
  orgId: string,
  projectId: string,
  params: GetTasksParams,
): Promise<PaginationResponse<TaskEntity>> => {
  const { data } = await api.get<undefined, ApiResponse<PaginationResponse<TaskEntity>>>(
    `/orgs/${orgId}/projects/${projectId}/tasks`,
    {
      params,
    },
  );

  return data;
};

export const getTasksQueryKey = (
  orgId: string | undefined | null,
  projectId: string | undefined | null,
  filters?: TasksQueryDto | null,
) => {
  const queryKey: unknown[] = ['tasks', 'list'];
  if (orgId) queryKey.push('org', orgId);
  if (projectId) queryKey.push('project', projectId);
  if (filters) queryKey.push('filters', filters);
  return queryKey;
};

export const getTasksQueryOptions = (
  orgId: string | undefined | null,
  projectId: string | undefined | null,
  filters: TasksQueryDto | undefined | null,
): UsePaginatedQueryOptions<TaskEntity> => {
  return {
    queryKey: getTasksQueryKey(orgId, projectId, filters),
    queryFn: ({ pageParam }) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      getTasks(orgId!, projectId!, {
        ...filters,
        ...pageParam,
      }),
    enabled: !!orgId && !!projectId,
  };
};

interface UseTasksOptions {
  queryConfig?: PaginatedQueryConfig<typeof getTasksQueryOptions>;
}

export const useTasks = (filters?: TasksQueryDto | null, { queryConfig }: UseTasksOptions = {}) => {
  const { orgId, projectId } = useScopeIds();
  return usePaginatedQuery({
    ...getTasksQueryOptions(orgId, projectId, filters),
    ...queryConfig,
  });
};

// Keep the old hook for backward compatibility
export const useGetTasks = (filters?: TasksQueryDto) => {
  const { orgId, projectId } = useScopeIds('org', 'project');

  return usePaginatedQuery({
    ...getTasksQueryOptions(orgId, projectId, filters),
  });
};
